import React, { useState } from 'react';
import { ListItem, ListItemText, Box, Typography, Chip, IconButton } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { convertToHumanReadableTime } from '../../utils/helpers';
import VideoStreamDialog from './VideoStreamDialog';
import NearestTechniciansDialog from './NearestTechniciansDialog';
import VehicleAlertMapModal from './VehicleAlertMapModal';
import CameraOfflineDialog from './CameraOfflineDialog';

// Priority level color mapping
const PRIORITY_COLORS = {
  'High': '#f44336',
  'Moderate': '#ff9800',
  'Low': '#4caf50'
};

// Updated functions for EventItem.js

// UPDATED: Separate detection checks for PD and ID
const EventItem = ({
  event,
  index,
  location,
  isHighSurveillance,
  onEventClick,
  getEventPriority,
  getCameraByIp,
  isReportView = false,
  httpsUrl: prophttpsUrl,
  currentUser: propCurrentUser = 'System',
  liveVtsClosedCache = {},
  onLiveVtsEventClosed
}) => {
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [isTechniciansDialogOpen, setIsTechniciansDialogOpen] = useState(false);
  const [isVehicleMapModalOpen, setIsVehicleMapModalOpen] = useState(false);
  const [isCameraOfflineDialogOpen, setIsCameraOfflineDialogOpen] = useState(false);

  // Vehicle alert detection - UPDATED to include new types
  const isVehicleAlert = event.isVehicleAlert || event.alertType;
  const isOffAreaAlert = event.isOffAreaAlert || event.alertType === 'off-area';
  const isOffRouteAlert = !isOffAreaAlert && (event.alertType === 'off-route');
  const isTamperAlert = event.alertType === 'tamper';
  const isOverSpeedAlert = event.alertType === 'overspeed';         // NEW
  const isUnauthorizedStoppageAlert = event.alertType === 'stoppage'; // NEW

  // VTS session-closed cache lookup
  // Key must match VehicleAlertMapModal's liveEventKey: `${vehicleNumber}_${timeStamp}`
  const vtsLiveKey = isVehicleAlert && event.vehicleNumber && event.timeStamp
    ? `${event.vehicleNumber}_${event.timeStamp}`
    : null;
  const isVtsClosed = vtsLiveKey ? !!liveVtsClosedCache[vtsLiveKey] : false;
  const vtsClosedRemark = vtsLiveKey ? (liveVtsClosedCache[vtsLiveKey] ?? null) : null;

  // UPDATED: Separate detection for PD and ID events
  const isPersonDetection = event.detectionType === 'PD' ||
    event.type === 'PD' ||
    event.originalMessage?.eventType === 'PD';

  const isIntrusionDetection = event.detectionType === 'ID' ||
    event.type === 'ID' ||
    event.originalMessage?.eventType === 'ID';

  const isLoadingStateEvent = event.type === 'LS' ||
    event.originalMessage?.eventType === 'LS';

  const isCameraTamperingEvent = event.originalMessage?.eventType === 'CT' ||
    event.eventType === 'Camera Tampering' ||
    event.isOfflineCamera;

  // NEW: Separate detection for offline camera events vs regular CT events
  const isOfflineCameraEvent = event.isOfflineCamera === true;
  const isRegularCTEvent = isCameraTamperingEvent && !isOfflineCameraEvent;

  // URL and user extraction functions (unchanged)
  const gethttpsUrl = () => {
    if (prophttpsUrl && typeof prophttpsUrl === 'string' && prophttpsUrl.trim() !== '') {
      return prophttpsUrl;
    }

    if (event.httpsUrl && typeof event.httpsUrl === 'string' && event.httpsUrl.trim() !== '') {
      return event.httpsUrl;
    }

    if (event.connectionInfo?.httpsUrl && typeof event.connectionInfo.httpsUrl === 'string' && event.connectionInfo.httpsUrl.trim() !== '') {
      return event.connectionInfo.httpsUrl;
    }

    if (event.originalMessage?.connectionInfo?.httpsUrl && typeof event.originalMessage.connectionInfo.httpsUrl === 'string' && event.originalMessage.connectionInfo.httpsUrl.trim() !== '') {
      return event.originalMessage.connectionInfo.httpsUrl;
    }

    return null;
  };

  const getCurrentUser = () => {
    if (propCurrentUser && typeof propCurrentUser === 'string' && propCurrentUser.trim() !== '') {
      return propCurrentUser;
    }

    if (event.currentUser && typeof event.currentUser === 'string' && event.currentUser.trim() !== '') {
      return event.currentUser;
    }

    return 'System';
  };

  // Extract the actual values to use
  const httpsUrl = gethttpsUrl();
  const currentUser = getCurrentUser();

  const timestamp = (() => {
    // For offline camera events, use the timestamp when the event was created
    if (isOfflineCameraEvent && event.timestamp) {
      try {
        const date = new Date(event.timestamp);
        return date.toLocaleString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      } catch (error) {
        console.error('Error formatting offline camera timestamp:', error);
        return event.readableTime || "N/A";
      }
    }

    // NEW: Check for eventTime from WebSocket message first (highest priority)
    if (event.originalMessage?.eventTime) {
      try {
        // Parse the eventTime format: "2025-08-28 13:00:58"
        const eventTime = new Date(event.originalMessage.eventTime.replace(' ', 'T'));
        return eventTime.toLocaleString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      } catch (error) {
        console.error('Error parsing eventTime:', error);
      }
    }

    // For vehicle alerts, use existing logic
    if (event.readableTime) {
      return event.readableTime;
    }

    if (event.isVehicleAlert && event.timeStamp) {
      return convertGMTtoIST(event.timeStamp);
    }

    // For regular camera events, use ID-based timestamp
    if (event.id) {
      return convertToHumanReadableTime(event.id.split("_")[0]);
    }

    return "N/A";
  })();

  // FIXED: Corrected GMT to IST conversion function
  const convertGMTtoIST = (gmtTimeString) => {
    try {
      const gmtDate = new Date(gmtTimeString);
      const istDate = new Date(gmtDate.getTime() + (5.5 * 60 * 60 * 1000));
      // FIXED: Use proper 12-hour format
      return istDate.toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // FIXED: Use 12-hour format with AM/PM
      });
    } catch (error) {
      console.error('Error converting GMT to IST:', error);
      return gmtTimeString;
    }
  };

  // Camera info extraction (unchanged)
  let cameraInfo = null;

  if (event.cameraInfo) {
    cameraInfo = event.cameraInfo;
  } else if (getCameraByIp) {
    cameraInfo = getCameraByIp(event.cameraIp);
  }

  // Coordinate validation (unchanged)
  let validatedCoordinates = null;
  if (cameraInfo) {
    const lat = cameraInfo.latitude;
    const lng = cameraInfo.longitude;

    const isLatValid = typeof lat === 'number' && !isNaN(lat) && isFinite(lat) && lat >= -90 && lat <= 90;
    const isLngValid = typeof lng === 'number' && !isNaN(lng) && isFinite(lng) && lng >= -180 && lng <= 180;

    if (isLatValid && isLngValid) {
      validatedCoordinates = {
        latitude: lat,
        longitude: lng,
        hasValidCoords: true,
        formattedLatitude: lat.toFixed(6),
        formattedLongitude: lng.toFixed(6)
      };
    }
  }

  // Priority and remarks (unchanged)
  let currentSeverity = event.severity || "normal";
  let currentRemarks = event.remarks || [];
  let isPriorityUpdated = event.priorityUpdated || false;

  const eventKey = event.id || event.eventId;

  if (!isReportView && getEventPriority && eventKey) {
    console.log(`🔍 Checking live priority for event: ${eventKey}`);

    // FIXED: Use exact event key matching only
    const livePriority = getEventPriority(eventKey);
    if (livePriority) {
      console.log(`✅ Found live priority for ${eventKey}:`, livePriority);
      currentSeverity = livePriority.severity;
      currentRemarks = livePriority.remarks || [];
      isPriorityUpdated = true;
    } else {
      console.log(`ℹ️ No live priority found for event: ${eventKey}`);
    }
  }

  // UPDATED: Enhanced background color logic with off-area priority and new alert types
  const getBackgroundColor = () => {
    // Priority colors take precedence
    if (currentSeverity && PRIORITY_COLORS[currentSeverity]) {
      return `${PRIORITY_COLORS[currentSeverity]}15`; // 15 = ~8% opacity
    }

    // Event type specific colors - OFF-AREA gets highest priority
    if (isOffAreaAlert) {
      return "rgba(255, 23, 68, 0.15)"; // Bright red background for off-area alerts
    }

    // NEW: Over Speed - Red background
    if (isOverSpeedAlert) {
      return "rgba(231, 76, 60, 0.15)";
    }

    // NEW: Unauthorized Stoppage - Orange background
    if (isUnauthorizedStoppageAlert) {
      return "rgba(243, 156, 18, 0.15)";
    }

    if (isIntrusionDetection) {
      return "rgba(255, 193, 7, 0.15)"; // Yellow background for ID events
    }

    if (isLoadingStateEvent) {
      return "rgba(255, 165, 0, 0.15)"; // Orange background for LS events
    }

    if (isRegularCTEvent) {
      return "rgba(255, 87, 34, 0.15)"; // Deep orange background for regular CT events
    }

    if (isOfflineCameraEvent) {
      return "rgba(244, 67, 54, 0.15)"; // Red background for offline camera events
    }

    // High surveillance areas
    if (isHighSurveillance) return "rgba(244,67,54,0.08)";

    // Default alternating colors
    return index % 2 === 0 ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.1)";
  };

  // UPDATED: Enhanced text color logic with off-area priority and new alert types
  const getTextColor = () => {
    // Priority colors take absolute precedence
    if (currentSeverity && PRIORITY_COLORS[currentSeverity]) {
      return PRIORITY_COLORS[currentSeverity];
    }

    // Event type specific colors (only if no priority set) - OFF-AREA gets highest priority
    if (isOffAreaAlert) {
      return "#FF1744"; // Bright red text for off-area alerts
    }

    // NEW: Over Speed - Red text
    if (isOverSpeedAlert) {
      return "#E74C3C";
    }

    // NEW: Unauthorized Stoppage - Orange text
    if (isUnauthorizedStoppageAlert) {
      return "#F39C12";
    }

    if (isIntrusionDetection) {
      return "#FFC107"; // Yellow text for ID events
    }

    if (isLoadingStateEvent) {
      return "#FF9500"; // Orange text for LS events
    }

    if (isRegularCTEvent) {
      return "#FF5722"; // Deep orange text for regular CT events
    }

    if (isOfflineCameraEvent) {
      return "#f44336"; // Red text for offline camera events
    }

    // High surveillance areas
    if (isHighSurveillance) return "#f44336";

    // Default white
    return "white";
  };

  // Check if this event has priority set
  const hasPriority = currentSeverity && PRIORITY_COLORS[currentSeverity];

  // Check if this event has comments
  const hasComments = currentRemarks && currentRemarks.length > 0;

  // Debug logging for priority state
  console.log(`🎨 Event ${eventKey} styling:`, {
    currentSeverity,
    hasPriority,
    hasComments,
    isPriorityUpdated,
    isOffAreaAlert,
    backgroundColor: getBackgroundColor(),
    textColor: getTextColor()
  });

  // Check if event has been sent to technicians
  const hasAlarmNotifications = currentRemarks && currentRemarks.some(remark =>
    remark.comment && remark.comment.includes('ALARM_SENT_TO_TECHNICIAN:')
  );

  // UPDATED: Enhanced display text calculation with off-area information and new alert types
  const getDisplayText = () => {
    // NEW: Special display for off-area alerts
    if (isOffAreaAlert) {
      return `${event.vehicleNumber} - OFF-AREA (${event.offAreaForArea || 'Unknown Area'})`;
    }

    // NEW: Over Speed alert
    if (isOverSpeedAlert) {
      return `${event.vehicleNumber} - OVER SPEED`;
    }

    // NEW: Unauthorized Stoppage alert
    if (isUnauthorizedStoppageAlert) {
      return `${event.vehicleNumber} - UNAUTHORIZED STOPPAGE`;
    }

    if (isVehicleAlert) {
      return `${event.vehicleNumber} - ${event.alertType?.toUpperCase() || 'ALERT'}`;
    }

    if (isOfflineCameraEvent) {
      return `📴 ${event.cameraName} - OFFLINE (${event.cameraIp})`;
    }

    if (isRegularCTEvent) {
      return `[CT] ${event.displayName || event.location || `Camera Tampering ${index + 1} - ${location}`}`;
    }

    // UPDATED: For camera events, show specific detection type
    let eventTypePrefix = '';
    if (isIntrusionDetection) {
      eventTypePrefix = '[ID] '; // Intrusion Detection prefix
    } else if (isPersonDetection) {
      eventTypePrefix = '[PD] '; // Person Detection prefix
    } else if (event.detectionType) {
      eventTypePrefix = `[${event.detectionType}] `; // Other detection types
    }

    return eventTypePrefix + (event.displayName || `Event ${index + 1} - ${location}`);
  };

  const displayText = getDisplayText();

  // Alarm feature validation (unchanged)
  const canUseAlarmFeature = () => {
    const eventId = event.id || event.eventId;

    const hasValidEventId = !!(eventId && typeof eventId === 'string' && eventId.trim() !== '');
    const hasValidhttpsUrl = !!(httpsUrl && typeof httpsUrl === 'string' && httpsUrl.trim() !== '');
    const hasValidCurrentUser = !!(currentUser && typeof currentUser === 'string' && currentUser.trim() !== '');

    return hasValidEventId && hasValidhttpsUrl && hasValidCurrentUser;
  };

  // Dialog handlers
  const handleVideoDialogClose = () => {
    setIsVideoDialogOpen(false);
  };

  const handleTechniciansDialogClose = () => {
    setIsTechniciansDialogOpen(false);
  };

  const handleVehicleMapModalClose = () => {
    setIsVehicleMapModalOpen(false);
  };

  const handleCameraOfflineDialogClose = () => {
    setIsCameraOfflineDialogOpen(false);
  };

  const handleVideoButtonClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsVideoDialogOpen(true);
  };

  const handleTechnicianButtonClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsTechniciansDialogOpen(true);
  };

  // UPDATED: handleListItemClick function - improved logging for separate PD/ID and off-area
  const handleListItemClick = (e) => {
    // Handle vehicle alerts differently - open map modal instead
    if (isVehicleAlert) {
      try {
        if (e.currentTarget && e.currentTarget.style) {
          const originalBg = e.currentTarget.style.backgroundColor;
          e.currentTarget.style.backgroundColor = '#1ABC9C22';
          setTimeout(() => {
            if (e.currentTarget && e.currentTarget.style) {
              e.currentTarget.style.backgroundColor = originalBg;
            }
          }, 1000);
        }
      } catch (error) {
        console.warn('Could not apply visual feedback:', error);
      }

      setIsVehicleMapModalOpen(true);
      return;
    }

    // NEW: Handle offline camera events - open camera offline dialog instead of image modal
    if (isOfflineCameraEvent) {
      try {
        if (e.currentTarget && e.currentTarget.style) {
          const originalBg = e.currentTarget.style.backgroundColor;
          e.currentTarget.style.backgroundColor = '#ff572222';
          setTimeout(() => {
            if (e.currentTarget && e.currentTarget.style) {
              e.currentTarget.style.backgroundColor = originalBg;
            }
          }, 1000);
        }
      } catch (error) {
        console.warn('Could not apply visual feedback:', error);
      }

      setIsCameraOfflineDialogOpen(true);
      return;
    }

    // Only trigger event click for camera events, not vehicle alerts or offline cameras
    if (!isVideoDialogOpen && !isTechniciansDialogOpen && onEventClick) {
      const enhancedEvent = {
        ...event,
        httpsUrl: httpsUrl,
        currentUser: currentUser
      };
      onEventClick(e, enhancedEvent);
    }
  };

  const getTechnicianButtonTooltip = () => {
    if (!canUseAlarmFeature()) {
      const missingItems = [];
      const eventId = event.id || event.eventId;

      if (!eventId || typeof eventId !== 'string' || eventId.trim() === '') {
        missingItems.push('Event ID');
      }
      if (!httpsUrl || typeof httpsUrl !== 'string' || httpsUrl.trim() === '') {
        missingItems.push('API URL');
      }
      if (!currentUser || typeof currentUser !== 'string' || currentUser.trim() === '') {
        missingItems.push('User Info');
      }

      return `❌ Alarm feature DISABLED - Missing: ${missingItems.join(', ')}\n\nDEBUG INFO:\nEvent ID: ${eventId || 'MISSING'}\nAPI URL: ${httpsUrl || 'MISSING'}\nUser: ${currentUser || 'MISSING'}`;
    }

    if (!cameraInfo && !event.cameraInfo) {
      return `⚠️ Camera not found (IP: ${event.cameraIp || 'Unknown'})\n✅ Alarm feature available`;
    }

    if (validatedCoordinates && validatedCoordinates.hasValidCoords) {
      const baseTooltip = `✅ Find Nearest Field Technicians\nCamera: ${event.cameraIp}\nCoordinates: ${validatedCoordinates.formattedLatitude}, ${validatedCoordinates.formattedLongitude}`;

      if (hasAlarmNotifications) {
        return `${baseTooltip}\n🚨 Alarm notifications already sent\n✅ Alarm feature ready`;
      }

      return `${baseTooltip}\n✅ Alarm feature ready`;
    }

    return `⚠️ Find Nearest Field Technicians\nCamera: ${event.cameraIp}\n⚠️ Camera coordinates invalid/missing\n${canUseAlarmFeature() ? '✅ Alarm feature ready' : '❌ Alarm feature disabled'}`;
  };

  const dialogCoordinates = validatedCoordinates || {
    latitude: null,
    longitude: null,
    hasValidCoords: false,
    formattedLatitude: 'N/A',
    formattedLongitude: 'N/A'
  };

  const enhancedEventForDialogs = {
    ...event,
    httpsUrl: httpsUrl,
    currentUser: currentUser,
    cameraInfo: cameraInfo || event.cameraInfo
  };

  return (
    <>
      <ListItem
        sx={{
          cursor: "pointer",
          background: `linear-gradient(90deg, ${getBackgroundColor()} 0%, rgba(20,28,40,0.45) 100%)`,
          marginBottom: "3px",
          marginLeft: "18px",
          borderRadius: "6px",
          padding: "6px 10px",
          border: hasPriority ? `1px solid ${PRIORITY_COLORS[currentSeverity]}40` :
            isOffAreaAlert ? '1px solid #FF174440' :
            isOverSpeedAlert ? '1px solid #E74C3C40' :
            isUnauthorizedStoppageAlert ? '1px solid #F39C1240' :
            isIntrusionDetection ? '1px solid #FFC10740' :
            isOfflineCameraEvent ? '1px solid #f4433640' :
            '1px solid rgba(255,255,255,0.04)',
          transition: 'transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease',
          '&:hover': {
            background: `linear-gradient(90deg, ${getBackgroundColor()} 0%, rgba(40,52,68,0.7) 100%)`,
            transform: 'translateX(3px)',
            boxShadow: `0 3px 10px ${getTextColor() === 'white' ? 'rgba(26,188,156,0.18)' : `${getTextColor()}33`}`,
          },
        }}
        onClick={handleListItemClick}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {hasPriority && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: PRIORITY_COLORS[currentSeverity]
                    }}
                  />
                )}
                {/* NEW: Bright red indicator dot for off-area alerts */}
                {isOffAreaAlert && !hasPriority && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: '#FF1744', // Bright red dot for off-area
                      animation: 'pulse 2s infinite' // Optional pulsing animation
                    }}
                  />
                )}
                {/* NEW: Red indicator dot for over speed */}
                {isOverSpeedAlert && !hasPriority && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: '#E74C3C' // Red dot for over speed
                    }}
                  />
                )}
                {/* NEW: Orange indicator dot for unauthorized stoppage */}
                {isUnauthorizedStoppageAlert && !hasPriority && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: '#F39C12' // Orange dot for stoppage
                    }}
                  />
                )}
                {/* Yellow indicator dot for ID events */}
                {isIntrusionDetection && !hasPriority && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: '#FFC107' // Yellow dot for ID events
                    }}
                  />
                )}
                {/* NEW: Red indicator dot for offline camera events */}
                {isOfflineCameraEvent && !hasPriority && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: '#f44336' // Red dot for offline camera events
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{ color: getTextColor() }}
                >
                  {displayText}
                </Typography>

                {/* Vehicle Alert Specific Information */}
                {isVehicleAlert && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
                      📍 {event.coordinates || 'N/A'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
                      🚛 {event.vehicleTransporter || 'Unknown Transporter'}
                    </Typography>
                    {/* Session-closed remark preview */}
                    {isVtsClosed && vtsClosedRemark && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#27ae60',
                          fontSize: '0.62rem',
                          fontStyle: 'italic',
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={vtsClosedRemark}
                      >
                        ✓ {vtsClosedRemark}
                      </Typography>
                    )}
                    {/* NEW: Show geofence info for off-area alerts */}
                    {isOffAreaAlert && event.geofenceId && (
                      <Typography variant="caption" sx={{ color: '#FF1744', fontSize: '0.65rem', fontWeight: 'bold' }}>
                      </Typography>
                    )}
                    {event.alertSubType && (
                      <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.65rem' }}>
                        ⚠️ {event.alertSubType}
                      </Typography>
                    )}
                    {event.allocatedGeofence && event.allocatedGeofence.length > 0 && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem' }}>
                        🏃 {event.allocatedGeofence.join(', ')}
                      </Typography>
                    )}
                  </Box>
                )}

                {hasPriority && (
                  <Chip
                    label={currentSeverity}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6rem',
                      bgcolor: PRIORITY_COLORS[currentSeverity],
                      color: 'white'
                    }}
                  />
                )}
                {/* NEW: Chip for off-area alerts */}
                {isOffAreaAlert && !hasPriority && (
                  <Chip
                    label="OFF-AREA"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6rem',
                      bgcolor: '#FF1744',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                )}
                {/* NEW: Chip for over speed */}
                {isOverSpeedAlert && !hasPriority && (
                  <Chip
                    label="SPEED"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6rem',
                      bgcolor: '#E74C3C',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                )}
                {/* NEW: Chip for unauthorized stoppage */}
                {isUnauthorizedStoppageAlert && !hasPriority && (
                  <Chip
                    label="STOPPAGE"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6rem',
                      bgcolor: '#F39C12',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                )}
                {/* Yellow chip for ID events */}
                {isIntrusionDetection && !hasPriority && (
                  <Chip
                    label="ID"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6rem',
                      bgcolor: '#FFC107', // Yellow background for ID chip
                      color: 'black' // Black text for better contrast on yellow
                    }}
                  />
                )}
                {/* NEW: Offline camera chip */}
                {isOfflineCameraEvent && !hasPriority && (
                  <Chip
                    label="OFFLINE"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6rem',
                      bgcolor: '#f44336', // Red background for offline chip
                      color: 'white'
                    }}
                  />
                )}
                {/* VTS session-closed chip — shows after the user closes the alert in the modal */}
                {isVtsClosed && (
                  <Chip
                    label="Closed"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6rem',
                      bgcolor: 'rgba(39,174,96,0.2)',
                      color: '#27ae60',
                      border: '1px solid rgba(39,174,96,0.45)',
                      fontWeight: 'bold'
                    }}
                  />
                )}
                {!isReportView && isPriorityUpdated && (
                  <Chip
                    label="Live"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.55rem',
                      bgcolor: '#1ABC9C',
                      color: 'white'
                    }}
                  />
                )}
                {hasAlarmNotifications && (
                  <Chip
                    label="🚨 Alarmed"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.55rem',
                      bgcolor: '#f44336',
                      color: 'white'
                    }}
                  />
                )}
                {hasComments && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      color: 'rgba(255,255,255,0.7)',
                      bgcolor: 'rgba(0,0,0,0.2)',
                      px: 0.5,
                      borderRadius: 1
                    }}
                  >
                    {currentRemarks.length} {currentRemarks.length === 1 ? 'comment' : 'comments'}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "0.75rem"
                  }}
                >
                  {timestamp}
                </Typography>

                {/* Only show technician button for camera events, not vehicle alerts or offline cameras */}
                {/* {!isVehicleAlert && !isOfflineCameraEvent && (
                  <IconButton
                    size="small"
                    onClick={handleTechnicianButtonClick}
                    sx={{
                      ml: 1,
                      color: !canUseAlarmFeature() ? '#666666' :
                        hasAlarmNotifications ? '#f44336' :
                          dialogCoordinates.hasValidCoords ? '#4caf50' : '#ff9800',
                      backgroundColor: !canUseAlarmFeature() ? 'rgba(102, 102, 102, 0.1)' :
                        hasAlarmNotifications ? 'rgba(244, 67, 54, 0.1)' :
                          dialogCoordinates.hasValidCoords ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                      border: !canUseAlarmFeature() ? '1px solid rgba(102, 102, 102, 0.3)' :
                        hasAlarmNotifications ? '1px solid rgba(244, 67, 54, 0.3)' :
                          dialogCoordinates.hasValidCoords ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(255, 152, 0, 0.3)',
                      '&:hover': {
                        bgcolor: !canUseAlarmFeature() ? 'rgba(102, 102, 102, 0.2)' :
                          hasAlarmNotifications ? 'rgba(244, 67, 54, 0.2)' :
                            dialogCoordinates.hasValidCoords ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                        border: !canUseAlarmFeature() ? '1px solid rgba(102, 102, 102, 0.5)' :
                          hasAlarmNotifications ? '1px solid rgba(244, 67, 54, 0.5)' :
                            dialogCoordinates.hasValidCoords ? '1px solid rgba(76, 175, 80, 0.5)' : '1px solid rgba(255, 152, 0, 0.5)'
                      }
                    }}
                    title={getTechnicianButtonTooltip()}
                  >
                    <EngineeringIcon fontSize="small" />
                  </IconButton>
                )} */}

                {/* Only show video button for camera events, not vehicle alerts or offline cameras */}
                {!isVehicleAlert && !isOfflineCameraEvent && (
                  <IconButton
                    size="small"
                    onClick={handleVideoButtonClick}
                    sx={{
                      ml: 1,
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                    title="View Live Camera Feed"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          }
        />
      </ListItem>

      {/* The dialogs should only render for regular camera events (not vehicle alerts or offline cameras) */}
      {!isVehicleAlert && !isOfflineCameraEvent && (
        <>
          {/* Video Stream Dialog */}
          <VideoStreamDialog
            open={isVideoDialogOpen}
            onClose={handleVideoDialogClose}
            event={enhancedEventForDialogs}
          />

          {/* Nearest Technicians Dialog */}
          <NearestTechniciansDialog
            open={isTechniciansDialogOpen}
            onClose={handleTechniciansDialogClose}
            event={enhancedEventForDialogs}
            latitude={dialogCoordinates.latitude}
            longitude={dialogCoordinates.longitude}
            cameraIp={event.cameraIp}
            hasValidCoords={dialogCoordinates.hasValidCoords}
            formattedCoordinates={dialogCoordinates.hasValidCoords ?
              `${dialogCoordinates.formattedLatitude}, ${dialogCoordinates.formattedLongitude}` :
              'N/A'
            }
          />
        </>
      )}

      {/* Vehicle Alert Map Modal - Only for vehicle alerts */}
      {isVehicleAlert && (
        <VehicleAlertMapModal
          open={isVehicleMapModalOpen}
          onClose={handleVehicleMapModalClose}
          event={event}
          liveClosedCache={liveVtsClosedCache}
          onLiveEventClosed={onLiveVtsEventClosed}
        />
      )}

      {/* Camera Offline Dialog - Only for offline camera events */}
      {isOfflineCameraEvent && (
        <CameraOfflineDialog
          open={isCameraOfflineDialogOpen}
          onClose={handleCameraOfflineDialogClose}
          event={event}
        />
      )}
    </>
  );
};

export default EventItem;